/**
 * GroupNode Component
 * Renders a group node that can contain child nodes inside
 * Uses React Flow's parent-child relationship for nested nodes
 */

import { memo, useCallback, useState } from 'react';
import { Handle, Position, NodeResizer } from '@xyflow/react';
import type { ActionNodeData } from '../types';

interface GroupNodeProps {
  data: ActionNodeData & {
    children?: Array<{
      codigoUnico: string;
      label: string;
      orden: number;
    }>;
    isExpanded?: boolean;
  };
  selected?: boolean;
}

const GroupNode = memo(({ data, selected }: GroupNodeProps) => {
  const {
    label,
    orden,
    children = [],
    orientation = 'TB',
    codigoUnico,
  } = data;

  const [isHovered, setIsHovered] = useState(false);
  const isHorizontal = orientation === 'LR';

  // Handle add inside group
  const handleAddInside = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (codigoUnico) {
      console.log('[GroupNode] Dispatching add-inside for:', codigoUnico);
      window.dispatchEvent(new CustomEvent('flow-add-inside-group', {
        detail: { codigoUnico },
      }));
    }
  }, [codigoUnico]);

  // Handle delete group
  const handleDelete = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (codigoUnico) {
      console.log('[GroupNode] Dispatching delete for:', codigoUnico);
      window.dispatchEvent(new CustomEvent('flow-delete-action', {
        detail: { codigoUnico },
      }));
    }
  }, [codigoUnico]);

  // Handle duplicate group
  const handleDuplicate = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (codigoUnico) {
      console.log('[GroupNode] Dispatching duplicate for:', codigoUnico);
      window.dispatchEvent(new CustomEvent('flow-duplicate-action', {
        detail: { codigoUnico },
      }));
    }
  }, [codigoUnico]);

  // Handle child click
  const handleChildClick = useCallback((childCodigoUnico: string) => {
    window.dispatchEvent(new CustomEvent('flow-select-action', {
      detail: { codigoUnico: childCodigoUnico },
    }));
  }, []);

  // Handle positions
  const targetPosition = isHorizontal ? Position.Left : Position.Top;
  const sourcePosition = isHorizontal ? Position.Right : Position.Bottom;

  const handleStyle = {
    width: 8,
    height: 8,
    background: '#fff',
    border: '2px solid #f59e0b',
    borderRadius: '50%',
  };

  // Calculate min height based on children
  const minHeight = Math.max(100, children.length * 50 + 60);

  return (
    <>
      {/* Resizer for expanding group */}
      <NodeResizer
        minWidth={180}
        minHeight={minHeight}
        isVisible={selected}
        lineClassName="group-resize-line"
        handleClassName="group-resize-handle"
      />

      {/* Input Handle */}
      <Handle
        type="target"
        position={targetPosition}
        style={{
          ...handleStyle,
          ...(isHorizontal ? { left: -4 } : { top: -4 }),
        }}
      />

      {/* Jump-in handle (left side) - target for saltarA jumps */}
      <Handle
        type="target"
        position={Position.Left}
        id="jump-in"
        style={{ width: 6, height: 6, background: 'transparent', border: 'none', left: -3, top: '50%', transform: 'translateY(-50%)' }}
      />

      {/* Jump-out handle (right side) - source for saltarA jumps */}
      <Handle
        type="source"
        position={Position.Right}
        id="jump-out"
        style={{ width: 6, height: 6, background: 'transparent', border: 'none', right: -3, top: '50%', transform: 'translateY(-50%)' }}
      />

      {/* Group container */}
      <div
        style={{
          background: '#fffbeb',
          borderTop: '3px solid #f59e0b',
          borderRight: '1px solid #fcd34d',
          borderBottom: '1px solid #fcd34d',
          borderLeft: '1px solid #fcd34d',
          borderRadius: 8,
          padding: 0,
          minWidth: 180,
          minHeight: minHeight,
          boxShadow: selected
            ? '0 0 0 2px rgba(245, 158, 11, 0.3)'
            : '0 2px 4px rgba(0,0,0,0.1)',
          position: 'relative',
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Action buttons - appear on hover */}
        {isHovered && (
          <div
            style={{
              position: 'absolute',
              top: -24,
              right: 4,
              display: 'flex',
              gap: 2,
              background: '#fff',
              borderRadius: 4,
              padding: 2,
              boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
              zIndex: 10,
            }}
          >
            <button
              onClick={handleAddInside}
              onMouseDown={(e) => e.stopPropagation()}
              title="Agregar dentro del grupo"
              style={{
                width: 20,
                height: 20,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'transparent',
                border: 'none',
                borderRadius: 3,
                cursor: 'pointer',
                color: '#f59e0b',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#fef3c7'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
            </button>
            <div style={{ width: 1, height: 16, background: '#e5e7eb', margin: '2px 2px' }} />
            <button
              onClick={handleDuplicate}
              onMouseDown={(e) => e.stopPropagation()}
              title="Duplicar grupo"
              style={{
                width: 20,
                height: 20,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'transparent',
                border: 'none',
                borderRadius: 3,
                cursor: 'pointer',
                color: '#6b7280',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#f3f4f6'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
              </svg>
            </button>
            <button
              onClick={handleDelete}
              onMouseDown={(e) => e.stopPropagation()}
              title="Eliminar grupo"
              style={{
                width: 20,
                height: 20,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'transparent',
                border: 'none',
                borderRadius: 3,
                cursor: 'pointer',
                color: '#ef4444',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#fef2f2'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
              </svg>
            </button>
          </div>
        )}

        {/* Group header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '8px 12px',
            borderBottom: '1px solid #fcd34d',
            background: '#fef3c7',
            borderRadius: '5px 5px 0 0',
            cursor: 'grab',
          }}
        >
          {/* Order badge */}
          <div
            style={{
              minWidth: 20,
              height: 20,
              borderRadius: 4,
              background: '#f59e0b',
              color: '#fff',
              fontSize: 11,
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {orden}
          </div>

          {/* Group icon */}
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#92400e" strokeWidth="2">
            <rect x="3" y="3" width="7" height="7" rx="1" />
            <rect x="14" y="3" width="7" height="7" rx="1" />
            <rect x="3" y="14" width="7" height="7" rx="1" />
            <rect x="14" y="14" width="7" height="7" rx="1" />
          </svg>

          {/* Label */}
          <span
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: '#92400e',
              flex: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {label || 'Grupo'}
          </span>

          {/* Type badge */}
          <span
            style={{
              fontSize: 9,
              color: '#92400e',
              background: '#fde68a',
              padding: '2px 6px',
              borderRadius: 3,
              fontWeight: 500,
            }}
          >
            GRUPO
          </span>
        </div>

        {/* Children container */}
        <div
          style={{
            padding: 8,
            minHeight: 50,
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
          }}
        >
          {children.length === 0 ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: 50,
                color: '#d97706',
                fontSize: 11,
                fontStyle: 'italic',
              }}
            >
              Arrastra acciones aquí
            </div>
          ) : (
            children.map((child, idx) => (
              <div
                key={child.codigoUnico}
                onClick={(e) => {
                  e.stopPropagation();
                  handleChildClick(child.codigoUnico);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '6px 8px',
                  background: '#fff',
                  border: '1px solid #fcd34d',
                  borderRadius: 4,
                  cursor: 'pointer',
                  transition: 'background 0.15s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#fef9c3'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; }}
              >
                {/* Child order */}
                <span
                  style={{
                    fontSize: 10,
                    color: '#92400e',
                    fontWeight: 600,
                  }}
                >
                  {orden}.{idx + 1}
                </span>

                {/* Child label */}
                <span
                  style={{
                    fontSize: 12,
                    color: '#78350f',
                    flex: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {child.label}
                </span>

                {/* Arrow icon */}
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Output Handle */}
      <Handle
        type="source"
        position={sourcePosition}
        style={{
          ...handleStyle,
          ...(isHorizontal ? { right: -4 } : { bottom: -4 }),
        }}
      />
    </>
  );
});

GroupNode.displayName = 'GroupNode';

export default GroupNode;
